import { Component, ChangeDetectorRef, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Subscription } from 'rxjs';

import { SkeletonModule } from 'primeng/skeleton';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { AutoCompleteModule } from 'primeng/autocomplete';

import { CommonService } from 'wire-frame';

import { AppointmentService } from '../../../service/admin/appointment.service';
import { ClinicService } from '../../../service/admin/clinic.service';
import { UserService } from '../../../service/admin/user.service';
import { ApiService } from '../../../service/admin/api.service';
import { ToasterService } from '../../../service/common/toaster.service';

interface KanbanColumn {
  key: string;
  label: string;
  accent: string;
  bg: string;
  icon: string;
}

@Component({
  selector: 'app-appointment-kanban',
  standalone: true,
  imports: [
    CommonModule, FormsModule,
    SkeletonModule, DialogModule, DropdownModule, AutoCompleteModule,
  ],
  providers: [AppointmentService, CommonService, ClinicService, UserService, ApiService],
  templateUrl: './appointment-kanban.component.html',
  styleUrl: './appointment-kanban.component.scss',
})
export class AppointmentKanbanComponent implements OnInit, OnDestroy {

  // ── Board state ─────────────────────────────────────────────────────────────
  public appointmentList: any[] = [];
  public searchTerm = '';
  public isLoading = false;
  public lastRefreshed: Date = new Date();
  public readonly today: string = AppointmentKanbanComponent.getTodayDate();
  public selectedDate: string = AppointmentKanbanComponent.getTodayDate();

  // ── Dialog state ─────────────────────────────────────────────────────────────
  public dialogVisible = false;
  public isSaving = false;
  public selectedPatient: any = null;
  public patientList: any[] = [];
  public clinicList: any[] = [];
  public doctorsList: any[] = [];
  public timeSlots: any[] = [];
  public selectedSlot: any = null;

  public appointment: any = this.emptyAppointment();

  public statusOptions = [
    { label: 'Scheduled',         value: 'scheduled' },
    { label: 'Assistant Doctor',  value: 'assistant_doctor' },
    { label: 'Doctor',            value: 'doctor' },
    { label: 'Medicine',          value: 'medicine' },
    { label: 'Reception',         value: 'reception' },
    { label: 'Medicine Scheduled',value: 'medicine_scheduled' },
    { label: 'Completed',         value: 'completed' },
    { label: 'Cancelled',         value: 'cancelled' },
  ];

  public typeOptions = [
    { label: 'New',        value: 'new' },
    { label: 'Follow Up',  value: 'follow up' },
    { label: 'Case Paper', value: 'case' },
  ];

  // ── Kanban columns ──────────────────────────────────────────────────────────
  public kanbanColumns: KanbanColumn[] = [
    { key: 'scheduled',          label: 'Scheduled',       accent: '#3b82f6', bg: '#eff6ff', icon: 'pi-calendar' },
    { key: 'assistant_doctor',   label: 'Asst. Doctor',    accent: '#8b5cf6', bg: '#f5f3ff', icon: 'pi-user-edit' },
    { key: 'doctor',             label: 'Doctor',          accent: '#0ea5e9', bg: '#f0f9ff', icon: 'pi-user' },
    { key: 'medicine',           label: 'Medicine',        accent: '#10b981', bg: '#ecfdf5', icon: 'pi-heart' },
    { key: 'medicine_scheduled', label: 'Med. Scheduled',  accent: '#84cc16', bg: '#f7fee7', icon: 'pi-clock' },
    { key: 'reception',          label: 'Reception',       accent: '#f97316', bg: '#fff7ed', icon: 'pi-building' },
    { key: 'completed',          label: 'Completed',       accent: '#059669', bg: '#d1fae5', icon: 'pi-check-circle' },
    { key: 'cancelled',          label: 'Cancelled',       accent: '#ef4444', bg: '#fee2e2', icon: 'pi-times-circle' },
  ];

  private refreshIntervalId: any;
  private refreshSubscription?: Subscription;
  private apiUrl: string;

  constructor(
    public common: CommonService,
    private toast: ToasterService,
    private appointmentService: AppointmentService,
    private clinicService: ClinicService,
    private userService: UserService,
    private apiService: ApiService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
  ) {
    this.apiUrl = this.apiService.getBaseUrl();
    this.common.breadCrum = {
      title: 'Appointment Kanban',
      icon: 'calendar',
      list: ['Appointment Kanban'],
    };
  }

  // ── Lifecycle ───────────────────────────────────────────────────────────────

  ngOnInit() {
    this.loadTodaysAppointments();
    this.loadLookups();
    this.refreshIntervalId = setInterval(() => this.loadTodaysAppointments(true), 60_000);
  }

  ngOnDestroy() {
    clearInterval(this.refreshIntervalId);
    this.refreshSubscription?.unsubscribe();
  }

  // ── Board data ──────────────────────────────────────────────────────────────

  loadTodaysAppointments(silent = false) {
    if (!silent) this.isLoading = true;
    const date = this.selectedDate;

    this.appointmentService.getAppointment({
      paginate: 'false',
      date,
      status: 'all',
      compact: 'true',
      sort_field: 'start_time',
      sort_direction: 'asc',
    }).subscribe({
      next: (res: any) => {
        const raw = Array.isArray(res?.data) ? res.data : [];
        this.appointmentList = raw
          .filter((a: any) => (a.appointment_date || '').slice(0, 10) === date)
          .map((a: any) => this.mapOne(a));
        this.lastRefreshed = new Date();
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to load appointments' });
      },
    });
  }

  refresh() { this.loadTodaysAppointments(); }

  onDateChange() { this.loadTodaysAppointments(); }

  goToToday() {
    this.selectedDate = AppointmentKanbanComponent.getTodayDate();
    this.loadTodaysAppointments();
  }

  shiftDate(days: number) {
    const d = new Date(this.selectedDate + 'T00:00:00');
    d.setDate(d.getDate() + days);
    const yyyy = d.getFullYear();
    const mm   = String(d.getMonth() + 1).padStart(2, '0');
    const dd   = String(d.getDate()).padStart(2, '0');
    this.selectedDate = `${yyyy}-${mm}-${dd}`;
    this.loadTodaysAppointments();
  }

  private loadLookups() {
    this.clinicService.getClinic().subscribe({
      next: (res: any) => {
        this.clinicList = Array.isArray(res.data)
          ? res.data.map((c: any) => ({ label: c.name, value: c.id }))
          : [];
        if (!this.appointment.clinic_id && this.clinicList.length) {
          this.appointment.clinic_id = this.clinicList[0].value;
        }
      },
    });
    this.userService.getDoctors().subscribe({
      next: (res: any) => {
        this.doctorsList = Array.isArray(res.data)
          ? res.data.map((d: any) => ({ label: d.name, value: d.id }))
          : [];
        if (!this.appointment.doctor_id && this.doctorsList.length) {
          this.appointment.doctor_id = this.doctorsList[0].value;
        }
      },
    });
  }

  // ── Add Appointment dialog ──────────────────────────────────────────────────

  openAddDialog() {
    this.appointment     = this.emptyAppointment();
    this.selectedPatient = null;
    this.selectedSlot    = null;
    this.patientList     = [];
    // Apply defaults from already-loaded lookups
    if (this.clinicList.length)   this.appointment.clinic_id  = this.clinicList[0].value;
    if (this.doctorsList.length)  this.appointment.doctor_id  = this.doctorsList[0].value;
    this.generateTimeSlots();
    this.dialogVisible   = true;
  }

  closeDialog() {
    this.dialogVisible = false;
    this.isSaving = false;
  }

  get canSave(): boolean {
    return !!(
      this.appointment.patient_id &&
      this.appointment.appointment_type &&
      this.appointment.status &&
      this.appointment.appointment_date &&
      this.appointment.start_time
    );
  }

  get availableSlotsCount(): number {
    return this.timeSlots.filter(s => !s.disabled).length;
  }

  get blockedSlotsCount(): number {
    return this.timeSlots.filter(s => s.disabled).length;
  }

  saveAppointment() {
    if (!this.canSave || this.isSaving) return;
    this.isSaving = true;

    this.appointmentService.addAppointment(this.appointment).subscribe({
      next: () => {
        this.toast.add({ severity: 'success', summary: 'Success', detail: 'Appointment added successfully' });
        this.closeDialog();
        this.loadTodaysAppointments(true);
      },
      error: () => {
        this.isSaving = false;
        this.toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to add appointment' });
      },
    });
  }

  searchPatients(event: any) {
    const query = event.query;
    this.http.get<any>(`${this.apiUrl}/patient/search`, {
      params: { q: query },
      ...this.getAuthHeaders(),
    }).subscribe({
      next: (res) => {
        this.patientList = (res.data || []).map((p: any) => ({
          ...p,
          full_name: `${p.first_name} ${p.last_name} (${p.patient_number})`,
        }));
        this.cdr.detectChanges();
      },
      error: () => {
        this.patientList = [];
        this.cdr.detectChanges();
      },
    });
  }

  onPatientSelect(event: any) {
    this.appointment.patient_id = event.value?.id || event.value?.patient_id;
    this.selectedPatient = event.value;
  }

  generateTimeSlots() {
    const slots = [];
    const now = new Date();
    for (let hour = 9; hour < 23; hour++) {
      const slotEnd = new Date();
      slotEnd.setHours(hour + 1, 0, 0, 0);
      slots.push({
        label:    `${this.formatAmPm(hour)} – ${this.formatAmPm(hour + 1)}`,
        start:    this.padHour(hour),
        end:      this.padHour(hour + 1),
        disabled: slotEnd <= now,
      });
    }

    if (this.appointment.appointment_date) {
      const [y, m, d] = this.appointment.appointment_date.split('-').map(Number);
      const sel   = new Date(y, m - 1, d);
      const today = new Date();
      const todayMid = new Date(today.getFullYear(), today.getMonth(), today.getDate());

      if (sel < todayMid)       slots.forEach(s => s.disabled = true);
      else if (sel > todayMid)  slots.forEach(s => s.disabled = false);
    }

    this.timeSlots = slots;
  }

  selectSlot(slot: any) {
    if (slot.disabled) return;
    this.selectedSlot = slot;
    this.appointment.start_time = slot.start;
    this.appointment.end_time   = slot.end;
  }

  // ── Computed ────────────────────────────────────────────────────────────────

  static getTodayDate(): string {
    return new Date(Date.now() + 19800000).toISOString().slice(0, 10);
  }

  get todayLabel(): string {
    return new Date(this.today + 'T00:00:00').toLocaleDateString('en-IN', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
  }

  get selectedDateLabel(): string {
    const diff = this.dateDiffFromToday(this.selectedDate);
    if (diff === 0) return 'Today';
    if (diff === -1) return 'Yesterday';
    if (diff === 1) return 'Tomorrow';
    return new Date(this.selectedDate + 'T00:00:00').toLocaleDateString('en-IN', {
      weekday: 'short', day: 'numeric', month: 'short',
    });
  }

  get selectedDateSub(): string {
    return new Date(this.selectedDate + 'T00:00:00').toLocaleDateString('en-IN', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
  }

  get isToday(): boolean { return this.selectedDate === this.today; }
  get isFuture(): boolean { return this.selectedDate > this.today; }

  private dateDiffFromToday(date: string): number {
    const d1 = new Date(this.today + 'T00:00:00').getTime();
    const d2 = new Date(date + 'T00:00:00').getTime();
    return Math.round((d2 - d1) / 86400000);
  }

  get lastRefreshedLabel(): string {
    return this.lastRefreshed.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  }

  get filteredList(): any[] {
    if (!this.searchTerm.trim()) return this.appointmentList;
    const t = this.searchTerm.toLowerCase();
    return this.appointmentList.filter(a =>
      (a.patient_name     || '').toLowerCase().includes(t) ||
      (a.doctor_name      || '').toLowerCase().includes(t) ||
      (a.appointment_type || '').toLowerCase().includes(t),
    );
  }

  get inProgressCount(): number {
    return ['scheduled', 'assistant_doctor', 'assistant_doctor_done', 'doctor', 'medicine', 'medicine_scheduled', 'reception']
      .reduce((acc, k) => acc + this.getColumnCount(k), 0);
  }

  // assistant_doctor column also shows assistant_doctor_done cards
  private columnStatusKeys(colKey: string): string[] {
    if (colKey === 'assistant_doctor') return ['assistant_doctor', 'assistant_doctor_done'];
    return [colKey];
  }

  getColumnAppointments(statusKey: string): any[] {
    const keys = this.columnStatusKeys(statusKey);
    return this.filteredList.filter(a => keys.includes((a.status || '').toLowerCase()));
  }

  getColumnCount(statusKey: string): number {
    const keys = this.columnStatusKeys(statusKey);
    return this.appointmentList.filter(a => keys.includes((a.status || '').toLowerCase())).length;
  }

  isAssistantDone(appt: any): boolean {
    return (appt.status || '').toLowerCase() === 'assistant_doctor_done';
  }

  getTypeClass(type: string): string {
    const n = (type || '').toLowerCase().replace(/\s+/g, '-');
    if (n === 'new')       return 'type-new';
    if (n === 'follow-up') return 'type-follow';
    if (n === 'case')      return 'type-case';
    return '';
  }

  formatTime(time: string): string {
    if (!time) return '--';
    const parts = time.split(':');
    const h = parseInt(parts[0], 10);
    const m = parts[1] || '00';
    return `${((h + 11) % 12) + 1}:${m} ${h >= 12 ? 'PM' : 'AM'}`;
  }

  /** Exact same logic as prescriptions.component → getWaitTimeLabel */
  getWaitTimeLabel(startTime: string): string {
    if (!startTime) return '';
    const now = new Date();

    // Handle time-only string e.g. "09:00:00"
    if (startTime.length <= 8 && startTime.includes(':')) {
      const timeParts = startTime.split(':');
      if (timeParts.length < 2) return '';
      const aptTime = new Date();
      aptTime.setHours(parseInt(timeParts[0], 10), parseInt(timeParts[1], 10), 0, 0);
      startTime = aptTime.toISOString();
    }

    const aptTime = new Date(startTime);
    if (isNaN(aptTime.getTime())) return '';

    const diffMs   = now.getTime() - aptTime.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 0)  return `In ${Math.abs(diffMins)}m`;
    if (diffMins === 0) return 'Just now';
    if (diffMins < 60) return `Waiting ${diffMins}m`;
    const hours = Math.floor(diffMins / 60);
    const mins  = diffMins % 60;
    return `Waiting ${hours}h ${mins}m`;
  }

  /** Exact same logic as prescriptions.component → getWaitTimeColorClass,
   *  mapped to the kanban's own SCSS modifier classes. */
  getWaitTimeClass(label: string): string {
    if (!label) return '';
    if (label.includes('In ') || label.includes('Just now')) return 'kb-waiting--soon';

    const matchMins  = label.match(/Waiting (\d+)m/);
    const matchHours = label.match(/Waiting (\d+)h/);

    if (matchHours) return 'kb-waiting--late';   // 1 h+ → red
    if (matchMins) {
      const mins = parseInt(matchMins[1], 10);
      if (mins < 15) return 'kb-waiting--soon';  // neutral / blue
      if (mins < 30) return 'kb-waiting--warn';  // amber
      return 'kb-waiting--late';                 // red
    }
    return '';
  }

  /** Only show the badge for in-progress statuses. */
  getWaitingLabel(appt: any): string {
    const inProgress = ['scheduled', 'assistant_doctor', 'assistant_doctor_done', 'doctor', 'medicine', 'medicine_scheduled', 'reception'];
    if (!inProgress.includes((appt.status || '').toLowerCase())) return '';
    return this.getWaitTimeLabel(appt.created_at);
  }

  trackByColumn(_: number, c: KanbanColumn) { return c.key; }
  trackByAppt(_: number, a: any)            { return a.id; }

  // ── Private helpers ─────────────────────────────────────────────────────────

  private emptyAppointment() {
    return {
      id: 0,
      clinic_id: this.clinicList?.[0]?.value || 1,
      doctor_id: this.doctorsList?.[0]?.value || 1,
      patient_id: 0,
      appointment_type: '',
      appointment_date: AppointmentKanbanComponent.getTodayDate(),
      start_time: '',
      end_time: '',
      collect_later: false,
      status: 'scheduled',
      notes: '',
    };
  }

  private getAuthHeaders() {
    const token = localStorage.getItem('jwtToken');
    return { headers: new HttpHeaders({ Authorization: `Bearer ${token}` }) };
  }

  private padHour(h: number): string {
    return `${String(h).padStart(2, '0')}:00`;
  }

  private formatAmPm(h: number): string {
    const suffix = h >= 12 ? 'PM' : 'AM';
    return `${((h + 11) % 12) + 1}:00 ${suffix}`;
  }

  /** Exact same logic as prescriptions.component → normalizePrescriptionMode */
  private normalizeMode(raw: any): 'OPD' | 'COURIER' | 'NRI' {
    if (raw === 'NRI') return 'NRI';
    if (raw === true || raw === 'true' || raw === 1 || raw === '1' ||
        raw === 'Courier' || raw === 'COURIER') return 'COURIER';
    return 'OPD';
  }

  getModeLabel(mode: string): string {
    if (mode === 'COURIER') return 'Courier';
    if (mode === 'NRI')     return 'NRI';
    return 'OPD';
  }

  getModeClass(mode: string): string {
    if (mode === 'COURIER') return 'kb-mode--courier';
    if (mode === 'NRI')     return 'kb-mode--nri';
    return 'kb-mode--opd';
  }

  private mapOne(a: any): any {
    const mode = this.normalizeMode(a?.is_courier);

    const cl = a?.prescription_collect_later ?? a?.collect_later;
    const collectLater = cl === true || cl === 1 || ['1', 'true'].includes(String(cl || '').trim().toLowerCase());

    const patientName =
      a?.patient_name ||
      [a?.patient?.first_name, a?.patient?.middle_name, a?.patient?.last_name].filter(Boolean).join(' ').trim();

    const doctorName =
      a?.doctor_name ||
      [a?.doctor?.first_name, a?.doctor?.last_name].filter(Boolean).join(' ').trim();

    const isFree = a?.is_free === true || a?.is_free === 1 || a?.patient?.is_free === 1;

    return {
      ...a,
      patient_name: patientName,
      doctor_name:  doctorName,
      mode,
      collectLater,
      is_free: isFree,
    };
  }
}
